/**
 * Delivery Note List Page
 * Epic 63: Szallitolevol lista + osszesito kartyak
 * Batch Invoice: Gyujtoszamla generation from selected delivery notes
 */

import type { DeliveryNoteStatus, DeliveryNoteType } from '@/api/delivery-notes';
import { generateBatchInvoice } from '@/api/delivery-notes';
import { useDeliveryNotes, useDeliveryNoteSummary } from '@/hooks/useDeliveryNotes';
import { useTranslation } from '@kgc/i18n';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const TYPE_COLORS: Record<DeliveryNoteType, string> = {
  STANDARD: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
  CONTRACT: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
  RENTAL_ACCESSORY: 'bg-orange-100 text-orange-800 dark:bg-orange-900/20 dark:text-orange-300',
  CREDIT: 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-300',
};

const TYPE_CARD_COLORS: Record<DeliveryNoteType, string> = {
  STANDARD: 'border-blue-500 bg-blue-50 dark:bg-blue-900/20',
  CONTRACT: 'border-green-500 bg-green-50 dark:bg-green-900/20',
  RENTAL_ACCESSORY: 'border-orange-500 bg-orange-50 dark:bg-orange-900/20',
  CREDIT: 'border-red-500 bg-red-50 dark:bg-red-900/20',
};

const TYPE_LABEL_KEYS: Record<DeliveryNoteType, string> = {
  STANDARD: 'invoice.deliveryNote.typeLabels.standard',
  CONTRACT: 'invoice.deliveryNote.typeLabels.contract',
  RENTAL_ACCESSORY: 'invoice.deliveryNote.typeLabels.rentalAccessory',
  CREDIT: 'invoice.deliveryNote.typeLabels.supplierCredit',
};

const STATUS_LABEL_KEYS: Record<DeliveryNoteStatus, string> = {
  OPEN: 'invoice.deliveryNote.statusLabels.open',
  INVOICED: 'invoice.deliveryNote.statusLabels.invoiced',
  SETTLED: 'invoice.deliveryNote.statusLabels.settled',
  CLOSED: 'invoice.deliveryNote.statusLabels.closed',
};

const STATUS_COLORS: Record<DeliveryNoteStatus, string> = {
  OPEN: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-300',
  INVOICED: 'bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-300',
  SETTLED: 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-300',
  CLOSED: 'bg-gray-100 text-gray-800 dark:bg-[#1f5f6b] dark:text-[#ccfbf1]',
};

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function DeliveryNoteListPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<DeliveryNoteType | ''>('');
  const [statusFilter, setStatusFilter] = useState<DeliveryNoteStatus | ''>('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const filters: Record<string, string> = {};
  if (typeFilter) filters['type'] = typeFilter;
  if (statusFilter) filters['status'] = statusFilter;

  const { notes, isLoading } = useDeliveryNotes(
    Object.keys(filters).length > 0
      ? (filters as { type?: DeliveryNoteType; status?: DeliveryNoteStatus })
      : undefined
  );
  const { summary } = useDeliveryNoteSummary();

  const deliveryNoteTypes: DeliveryNoteType[] = [
    'STANDARD',
    'CONTRACT',
    'RENTAL_ACCESSORY',
    'CREDIT',
  ];

  // Derive selected notes from IDs
  const selectedNotes = useMemo(
    () => notes.filter(n => selectedIds.has(n.id)),
    [notes, selectedIds]
  );

  // Check if all selected notes belong to the same partner and are OPEN
  const batchValidation = useMemo(() => {
    if (selectedNotes.length === 0) {
      return { valid: false, reason: 'noSelection' as const };
    }

    const nonOpen = selectedNotes.filter(n => n.status !== 'OPEN');
    if (nonOpen.length > 0) {
      return { valid: false, reason: 'notOpen' as const };
    }

    const firstNote = selectedNotes[0];
    if (!firstNote) {
      return { valid: false, reason: 'noSelection' as const };
    }
    const partnerId = firstNote.partnerId;
    const hasMixedPartners = selectedNotes.some(n => n.partnerId !== partnerId);
    if (hasMixedPartners) {
      return { valid: false, reason: 'mixedPartners' as const };
    }

    const totalAmount = selectedNotes.reduce((sum, n) => sum + n.totalAmount, 0);
    return {
      valid: true,
      reason: 'ok' as const,
      partnerName: firstNote.partnerName,
      totalAmount,
      count: selectedNotes.length,
    };
  }, [selectedNotes]);

  const handleToggleSelect = useCallback((noteId: string, event: React.MouseEvent) => {
    event.stopPropagation();
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(noteId)) {
        next.delete(noteId);
      } else {
        next.add(noteId);
      }
      return next;
    });
  }, []);

  const handleToggleSelectAll = useCallback(() => {
    const openNotes = notes.filter(n => n.status === 'OPEN');
    if (selectedIds.size === openNotes.length && openNotes.length > 0) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(openNotes.map(n => n.id)));
    }
  }, [notes, selectedIds.size]);

  const handleGenerateBatchInvoice = useCallback(async () => {
    if (!batchValidation.valid) return;

    setIsGenerating(true);
    try {
      const response = await generateBatchInvoice({
        noteIds: Array.from(selectedIds),
      });
      const result = response.data;

      setSelectedIds(new Set());
      setShowConfirmDialog(false);

      // Navigate to the created invoice
      navigate(`/invoices/${result.invoice.id}`);
    } catch (err) {
      // Error is shown via toast or alert — keep dialog open
      setIsGenerating(false);
      const message = err instanceof Error ? err.message : String(err);
      alert(message);
    }
  }, [batchValidation.valid, selectedIds, navigate]);

  const openNoteCount = notes.filter(n => n.status === 'OPEN').length;

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">{t('invoice.deliveryNote.title')}</h1>
        <div className="flex gap-2">
          {selectedIds.size > 0 && batchValidation.valid && (
            <button
              onClick={() => setShowConfirmDialog(true)}
              className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
            >
              {t('invoice.deliveryNote.generateBatchInvoice')} ({selectedIds.size})
            </button>
          )}
          {selectedIds.size > 0 &&
            !batchValidation.valid &&
            batchValidation.reason === 'mixedPartners' && (
              <span className="flex items-center rounded-lg bg-red-100 px-3 py-2 text-sm text-red-800 dark:bg-red-900/20 dark:text-red-300">
                {t('invoice.deliveryNote.batchPartnerMismatch')}
              </span>
            )}
          <button
            onClick={() => navigate('/delivery-notes/new')}
            className="rounded-lg bg-primary px-4 py-2 text-white hover:bg-primary/90"
          >
            {t('invoice.deliveryNote.newDeliveryNote')}
          </button>
        </div>
      </div>

      {/* Selection info bar */}
      {selectedIds.size > 0 && (
        <div className="flex items-center justify-between rounded-lg bg-blue-50 px-4 py-2 dark:bg-blue-900/20">
          <span className="text-sm font-medium text-blue-800 dark:text-blue-300">
            {t('invoice.deliveryNote.selectedNotes', { count: selectedIds.size })}
          </span>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="text-sm text-blue-600 underline hover:text-blue-800 dark:text-blue-400"
          >
            {t('invoice.deliveryNote.cancel')}
          </button>
        </div>
      )}

      {/* Summary Cards */}
      {summary && (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-4">
          {deliveryNoteTypes.map(type => (
            <div key={type} className={`rounded-lg border-l-4 p-4 ${TYPE_CARD_COLORS[type]}`}>
              <div className="text-sm font-medium text-gray-600 dark:text-[#ccfbf1]">
                {t(TYPE_LABEL_KEYS[type] ?? type)}
              </div>
              <div className="mt-1 text-2xl font-bold">{summary.byType[type]}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filters */}
      <div className="flex gap-4">
        <select
          value={typeFilter}
          onChange={e => setTypeFilter(e.target.value as DeliveryNoteType | '')}
          className="rounded-lg border px-3 py-2 dark:border-[#2d7d8a] dark:bg-[#1a4f5c] dark:text-[#ccfbf1]"
        >
          <option value="">{t('invoice.deliveryNote.allTypes')}</option>
          {deliveryNoteTypes.map(type => (
            <option key={type} value={type}>
              {t(TYPE_LABEL_KEYS[type] ?? type)}
            </option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value as DeliveryNoteStatus | '')}
          className="rounded-lg border px-3 py-2 dark:border-[#2d7d8a] dark:bg-[#1a4f5c] dark:text-[#ccfbf1]"
        >
          <option value="">{t('invoice.deliveryNote.allStatuses')}</option>
          <option value="OPEN">{t('invoice.deliveryNote.statusLabels.open')}</option>
          <option value="INVOICED">{t('invoice.deliveryNote.statusLabels.invoiced')}</option>
          <option value="SETTLED">{t('invoice.deliveryNote.statusLabels.settled')}</option>
          <option value="CLOSED">{t('invoice.deliveryNote.statusLabels.closed')}</option>
        </select>
      </div>

      {/* Table */}
      {isLoading ? (
        <div className="py-12 text-center text-gray-500 dark:text-[#ccfbf1]">
          {t('invoice.deliveryNote.loading')}
        </div>
      ) : notes.length === 0 ? (
        <div className="py-12 text-center text-gray-500 dark:text-[#ccfbf1]">
          {t('invoice.deliveryNote.noDeliveryNotes')}
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-[#2d7d8a]">
            <thead className="bg-gray-50 dark:bg-[#1f5f6b]">
              <tr>
                <th className="px-2 py-3 text-center">
                  <input
                    type="checkbox"
                    checked={openNoteCount > 0 && selectedIds.size === openNoteCount}
                    onChange={handleToggleSelectAll}
                    className="h-4 w-4 rounded"
                    title={t('invoice.deliveryNote.selectForBatch')}
                  />
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                  {t('invoice.deliveryNote.number')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                  {t('invoice.deliveryNote.typeFilterLabel')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                  {t('table.partner')}
                </th>
                <th className="px-4 py-3 text-right text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                  {t('invoice.deliveryNote.amountLabel')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                  {t('invoice.deliveryNote.statusLabel')}
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium text-gray-500 dark:text-[#ccfbf1]">
                  {t('invoice.deliveryNote.date')}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-[#2d7d8a]">
              {notes.map(note => (
                <tr
                  key={note.id}
                  onClick={() => navigate(`/delivery-notes/${note.id}`)}
                  className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-[#0f3d47] ${
                    selectedIds.has(note.id) ? 'bg-blue-50 dark:bg-blue-900/10' : ''
                  }`}
                >
                  <td className="px-2 py-3 text-center">
                    {note.status === 'OPEN' ? (
                      <input
                        type="checkbox"
                        checked={selectedIds.has(note.id)}
                        onClick={e => handleToggleSelect(note.id, e)}
                        onChange={() => {}}
                        className="h-4 w-4 rounded"
                      />
                    ) : (
                      <span className="inline-block h-4 w-4" />
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm font-medium">{note.noteNumber}</td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${TYPE_COLORS[note.type]}`}
                    >
                      {t(TYPE_LABEL_KEYS[note.type] ?? note.type)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">{note.partnerName}</td>
                  <td className="px-4 py-3 text-right text-sm font-medium">
                    {formatCurrency(note.totalAmount)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <span
                      className={`rounded-full px-2 py-1 text-xs font-medium ${STATUS_COLORS[note.status]}`}
                    >
                      {t(STATUS_LABEL_KEYS[note.status] ?? note.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 dark:text-[#ccfbf1]">
                    {new Date(note.createdAt).toLocaleDateString('hu-HU')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && batchValidation.valid && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="mx-4 max-w-md rounded-lg bg-white p-6 shadow-xl dark:bg-[#0d2f38]">
            <h3 className="mb-4 text-lg font-bold">
              {t('invoice.deliveryNote.batchConfirmTitle')}
            </h3>
            {batchValidation.valid && (
              <p className="mb-6 text-sm text-gray-600 dark:text-[#ccfbf1]">
                {t('invoice.deliveryNote.batchConfirmMessage', {
                  count: batchValidation.count ?? 0,
                  partnerName: batchValidation.partnerName ?? '',
                  amount: formatCurrency(batchValidation.totalAmount ?? 0),
                })}
              </p>
            )}
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowConfirmDialog(false)}
                disabled={isGenerating}
                className="rounded-lg border px-4 py-2 text-sm hover:bg-gray-50 dark:border-[#2d7d8a] dark:hover:bg-[#0f3d47]"
              >
                {t('invoice.deliveryNote.cancel')}
              </button>
              <button
                onClick={handleGenerateBatchInvoice}
                disabled={isGenerating}
                className="rounded-lg bg-green-600 px-4 py-2 text-sm text-white hover:bg-green-700 disabled:opacity-50"
              >
                {isGenerating
                  ? t('invoice.deliveryNote.batchGenerating')
                  : t('invoice.deliveryNote.batchConfirmGenerate')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
